import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { ScanSearch, ArrowRight, ChevronRight, XCircle, CheckCircle2, Users, BarChart3, X, Search } from 'lucide-react';
import { useClasseContext } from '../../context/ClasseContext';
import { useUser } from '../../context/UserContext';
import { getAccessType } from '../../utils/permissions';
import { useBilanAttendus } from '../../hooks/fetch_api_hook/useBilanAttendus';
import { analyseTanoEd, ETATS } from '../../utils/analyseTanoEd';
import { classerAttendus, normaliser } from '../../utils/rechercheAttendus';
import MathText from '../VueQuestion/MathText';
import HtmlKatex from '../VueQuestion/HtmlKatex';

// « L'analyse de TanoEd », le tableau de bord d'une classe ou d'un groupe (V1 du back, 04/10/2026,
// voir utils/analyseTanoEd.js). À l'arrivée, une ligne par proposition : les priorités que le back a
// choisies, puis les signaux, repliés quand il y a des priorités ; puis « Choisir un attendu » : tous
// les attendus travaillés, avec une recherche. Un clic ouvre le détail de l'attendu dans une fenêtre :
// l'erreur sur un vrai exemple et la règle à reprendre côte à côte, la liste d'élèves de la ligne,
// un bouton vers Créer (décision du prof, épreuve de Musk, 04/10). Une seule liste par ligne : le
// même nombre sur la ligne, dans la fenêtre et vers Créer (08/10). Caché sans accès complet, comme
// Savoirs.

const pluriel = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;
const majuscule = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const ETAT_LIBELLE = Object.fromEntries(ETATS);
// Le petit chevron d'un bloc repliable, à la place du triangle du navigateur (à la taille de la police du
// bloc, il paraissait énorme à côté d'un titre en petit) : il tourne quand son propre bloc est ouvert.
const CHEVRON_OUVERT = 'transition [details[open]>summary_&]:rotate-90';
const COULEUR_ETAT = {
    a_reprendre: 'bg-rose-500', progresse: 'bg-amber-400', a_confirmer: 'bg-sky-400',
    acquis: 'bg-emerald-500', a_verifier: 'bg-violet-400', sans_donnee: 'bg-slate-300',
};

const Faux = ({ children }) => (
    <MathText className="font-semibold text-rose-700">{children === '' ? 'rien' : children}</MathText>
);
const Juste = ({ children }) => <MathText className="font-semibold text-blue-800">{children}</MathText>;
const Attendu = () => <span className="text-slate-400"> · attendu </span>;

/** Les réponses à lignes (trous, paires) : seulement ce qui est faux, en regard de l'attendu. */
const Lignes = ({ lu }) => (
    <ul className="flex flex-col gap-1 text-xs text-slate-700">
        {lu.lignes.map((l, i) => (
            <li key={`${i}-${l.cle ?? l.bonne}`}>
                {l.cle ? <><MathText>{l.cle}</MathText> : </> : null}<Faux>{l.donnee}</Faux><Attendu /><Juste>{l.bonne}</Juste>
            </li>
        ))}
    </ul>
);

/** Deux cases : la réponse de l'élève, la réponse attendue. */
const Cases = ({ donnee, bonne }) => (
    <div className="grid grid-cols-2 gap-2">
        <div className="flex min-w-0 flex-col gap-0.5 rounded-lg bg-white px-3 py-2">
            <span className="text-[11px] text-slate-500">Réponse de l'élève</span>
            <span className="text-base"><Faux>{donnee}</Faux></span>
        </div>
        <div className="flex min-w-0 flex-col gap-0.5 rounded-lg bg-white px-3 py-2">
            <span className="text-[11px] text-slate-500">Réponse attendue</span>
            <span className="text-base"><Juste>{bonne}</Juste></span>
        </div>
    </div>
);

/** Un vrai exemple : l'énoncé, puis ce que l'élève a répondu en regard de l'attendu. */
const Exemple = ({ exemple }) => {
    const { lu } = exemple;
    return (
        <div className="flex min-w-0 flex-col gap-2">
            {lu.enonce ? <HtmlKatex html={lu.enonce} className="block text-sm font-semibold text-slate-800" /> : null}
            {lu.forme === 'simple' ? <Cases donnee={lu.donnee} bonne={lu.bonne} /> : null}
            {lu.forme === 'multiple' ? <Cases donnee={lu.donnee.join(', ')} bonne={lu.bonne.join(', ')} /> : null}
            {lu.forme === 'vf' ? lu.lignes.map((l) => (
                <div key={l.phrase} className="flex flex-col gap-1.5">
                    <p className="text-xs text-slate-700">« <MathText>{l.phrase}</MathText> »</p>
                    <Cases donnee={l.donnee} bonne={l.bonne} />
                </div>
            )) : null}
            {lu.forme === 'trous' || lu.forme === 'paires' ? <Lignes lu={lu} /> : null}
        </div>
    );
};

const avecUneErreur = (n) => pluriel(n, 'élève avec une erreur récente', 'élèves avec une erreur récente');
/** Le nombre de la ligne : celui de sa liste d'élèves, la même dans la fenêtre et vers Créer. */
const compte = (carte) => {
    const n = carte.eleves.length;
    if (!n) return '';
    return carte.listeDuSignal ? avecUneErreur(n) : pluriel(n, 'élève à reprendre', 'élèves à reprendre');
};
const niveauHors = (carte) => (carte.horsNiveau && carte.niveau ? `${String(carte.niveau).replace('eme', 'e')} · ` : '');
/** Les besoins d'un attendu dans « Choisir un attendu », même un ou deux élèves sans alerte. */
const besoins = (f) => [
    f.aReprendre.length ? `${f.aReprendre.length} à reprendre` : '',
    f.signal.length ? avecUneErreur(f.signal.length) : '',
].filter(Boolean).join(' · ');

/** La liste de la ligne, en pastilles : les élèves à reprendre, ou, sans élève à reprendre, ceux qui se sont
 * trompés avec leur état réel. Dans une liste d'élèves à reprendre, l'état dirait dix fois la même chose : seul
 * « 2 jours » reste, il marque un élève persistant. */
const ListeEleves = ({ carte }) => {
    if (!carte.eleves.length) return null;
    return (
        <div className="flex flex-col gap-1.5">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                <Users aria-hidden="true" className="h-3.5 w-3.5" />
                {carte.listeDuSignal ? 'Élèves avec une erreur récente' : 'Élèves à reprendre'} ({carte.eleves.length})
            </p>
            <ul className="flex flex-wrap gap-1.5">
                {carte.eleves.map((e) => {
                    const detail = [carte.listeDuSignal ? majuscule(ETAT_LIBELLE[e.etat]) : '', e.persistant ? '2 jours' : ''].filter(Boolean).join(' · ');
                    return (
                        <li key={e.id} className="flex items-start gap-1.5 rounded-lg bg-slate-50 px-2.5 py-1.5">
                            <span aria-hidden="true" className={`mt-1 h-2 w-2 shrink-0 rounded-full ${COULEUR_ETAT[e.etat] || COULEUR_ETAT.sans_donnee}`} />
                            <span className="flex flex-col">
                                <span className="text-xs font-semibold text-slate-800">{e.nom}</span>
                                {detail ? <span className="text-[11px] text-slate-500">{detail}</span> : null}
                            </span>
                        </li>
                    );
                })}
            </ul>
            {carte.eleves.some((e) => e.persistant) ? (
                <p className="text-[11px] text-slate-500">« 2 jours » : difficulté retrouvée sur deux jours distincts.</p>
            ) : null}
        </div>
    );
};

/** Le détail d'un attendu : l'erreur et la règle côte à côte, les élèves concernés, le bouton. */
const CorpsCarte = ({ carte, ouvrirCreer }) => {
    const d = carte.diagnostic;
    const principale = d ? d.erreurs[0] : null;
    const exemple = carte.exemples[0];
    return (
        <div className="flex flex-col gap-3">
            {d || exemple ? (
                <div className="flex flex-wrap gap-3">
                    <div className="flex min-w-[14rem] flex-1 flex-col gap-2 rounded-xl bg-rose-50 p-3">
                        <p className="flex items-center gap-1.5 text-xs font-bold text-rose-700">
                            <XCircle aria-hidden="true" className="h-4 w-4" />
                            {d ? 'Erreur observée récemment' : 'Exemple d\'erreur observée'}
                        </p>
                        {principale ? (
                            <div className="flex flex-col gap-0.5">
                                <p className="text-sm font-semibold text-slate-800"><MathText>{principale.titre}</MathText></p>
                                {principale.sousTitre ? <p className="text-xs text-slate-700"><MathText>{principale.sousTitre}</MathText></p> : null}
                                {d.erreurs.length > 1 ? (
                                    <p className="text-[11px] text-slate-500">et {pluriel(d.erreurs.length - 1, 'autre erreur', 'autres erreurs')} de la même règle</p>
                                ) : null}
                            </div>
                        ) : null}
                        {exemple ? <Exemple exemple={exemple} /> : null}
                    </div>
                    {/* La règle de l'attendu, écrite telle qu'elle s'enseigne : ce que le prof reprend. */}
                    {d && d.regle ? (
                        <div className="flex min-w-[14rem] flex-1 flex-col gap-2 rounded-xl bg-emerald-50 p-3">
                            <p className="flex items-center gap-1.5 text-xs font-bold text-emerald-700">
                                <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
                                Règle à reprendre
                            </p>
                            <p className="text-sm text-slate-800"><MathText>{d.regle}</MathText></p>
                        </div>
                    ) : null}
                </div>
            ) : (
                // Une priorité peut ne venir que de notes : on le dit, sans réponse ni règle inventées.
                <p className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
                    {carte.aReprendre.length
                        ? 'Difficulté repérée dans les résultats. Aucun détail de réponse récent ne permet de préciser l\'erreur.'
                        : 'Aucun élève à reprendre ni d\'erreur récente avec les données disponibles.'}
                </p>
            )}
            <ListeEleves carte={carte} />
            {carte.cible.eleves.length ? (
                <button
                    type="button"
                    onClick={() => ouvrirCreer(carte)}
                    className="flex items-center justify-center gap-1.5 rounded-lg bg-blue-700 px-3 py-2 text-xs font-bold text-white hover:bg-blue-800 transition"
                >
                    Préparer une activité <ArrowRight className="w-3.5 h-3.5" />
                </button>
            ) : null}
        </div>
    );
};

/** Une proposition : l'attendu, combien d'élèves, l'erreur la plus observée chez eux ; un clic ouvre son détail. */
const Proposition = ({ carte, onOuvrir }) => {
    const d = carte.diagnostic;
    const principale = d ? d.erreurs[0] : null;
    return (
        <li>
            <button
                type="button"
                onClick={(e) => onOuvrir(carte, e.currentTarget)}
                className="group flex w-full items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left shadow-sm transition hover:border-blue-300 hover:shadow"
            >
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="flex items-start justify-between gap-3">
                        {/* Titre et lignes passent par MathText : toute lettre s'écrit en KaTeX. */}
                        <span className="line-clamp-1 min-w-0 text-sm font-bold text-slate-800"><MathText>{carte.titre}</MathText></span>
                        <span className="shrink-0 text-xs font-semibold text-slate-600">{niveauHors(carte)}{compte(carte)}</span>
                    </span>
                    {principale ? (
                        <span className="text-xs text-slate-600">
                            Erreur la plus observée : <MathText>{principale.titre}</MathText>
                        </span>
                    ) : null}
                    {d && d.erreurs.length > 1 ? (
                        <span className="text-[11px] text-slate-500">et {pluriel(d.erreurs.length - 1, 'autre erreur', 'autres erreurs')} de la même règle</span>
                    ) : null}
                    {!d && !carte.exemples.length && carte.aReprendre.length ? (
                        <span className="text-xs text-slate-600">Difficulté repérée dans les résultats</span>
                    ) : null}
                </span>
                <ChevronRight aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-slate-400 group-hover:text-blue-600" />
            </button>
        </li>
    );
};

/** Le détail d'un attendu dans une fenêtre : la croix, Échap ou un clic à côté la ferment. */
const FenetreAttendu = ({ carte, onFermer, ouvrirCreer }) => {
    const fermerRef = useRef(null);
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onFermer(); };
        window.addEventListener('keydown', onKey);
        if (fermerRef.current) fermerRef.current.focus();
        return () => window.removeEventListener('keydown', onKey);
    }, [onFermer]);
    const idTitre = `fenetre-attendu-${carte.attendu}`;
    const lePlus = compte(carte);
    // Dans la page elle-même : aucun parent (débordement caché, effet CSS) ne peut couper la fenêtre.
    return createPortal(
        <div className="fixed inset-0 z-[10010] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm sm:p-6" onClick={onFermer}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={idTitre}
                className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-100 bg-white px-6 py-4">
                    <div className="flex min-w-0 flex-col gap-1">
                        <h2 id={idTitre} className="text-base font-bold leading-snug text-slate-800"><MathText>{carte.titre}</MathText></h2>
                        {lePlus ? (
                            <p className="flex items-center gap-1 text-xs font-semibold text-slate-600">
                                <Users aria-hidden="true" className="h-3.5 w-3.5" />
                                {niveauHors(carte)}{lePlus}
                            </p>
                        ) : null}
                    </div>
                    <button
                        ref={fermerRef}
                        type="button"
                        onClick={onFermer}
                        aria-label="Fermer"
                        className="shrink-0 rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                    >
                        <X aria-hidden="true" className="h-5 w-5" />
                    </button>
                </div>
                <div className="px-6 py-4">
                    <CorpsCarte carte={carte} ouvrirCreer={ouvrirCreer} />
                </div>
            </div>
        </div>,
        document.body,
    );
};

/** Tous les attendus travaillés, repliés, avec leurs besoins et une recherche ; un clic ouvre le détail. */
const ChoisirAttendu = ({ tous, onOuvrir }) => {
    const [texte, setTexte] = useState('');
    // En tapant, chaque début de mot doit se trouver dans l'intitulé, sans accents ni majuscules (« rel »
    // trouve les relatifs) ; en plus, la recherche de « Coller mes notes », par mots et leurs variantes.
    // L'ordre reste celui du back.
    const visibles = useMemo(() => {
        const brut = texte.trim();
        if (!brut) return tous;
        const fragments = normaliser(brut).split(' ').filter(Boolean);
        const parMots = new Set(classerAttendus(tous.map((f) => ({ id: f.attendu, description: f.titre })), brut, { max: tous.length }).map((r) => r.id));
        return tous.filter((f) => {
            const titre = normaliser(f.titre);
            return fragments.every((m) => titre.includes(m)) || parMots.has(f.attendu);
        });
    }, [tous, texte]);
    if (!tous.length) return null;
    return (
        <details className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <summary className="flex cursor-pointer list-none items-center gap-1.5 text-sm font-bold text-slate-800 [&::-webkit-details-marker]:hidden">
                <ChevronRight aria-hidden="true" className={`h-4 w-4 shrink-0 text-slate-400 ${CHEVRON_OUVERT}`} />
                Choisir un attendu · {pluriel(tous.length, 'attendu travaillé', 'attendus travaillés')}
            </summary>
            <label className="mt-3 flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 focus-within:border-blue-400">
                <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-slate-400" />
                <input
                    type="search"
                    value={texte}
                    onChange={(e) => setTexte(e.target.value)}
                    placeholder="Rechercher un attendu…"
                    aria-label="Rechercher un attendu"
                    className="w-full bg-transparent text-xs text-slate-800 outline-none"
                />
            </label>
            {visibles.length ? (
                <ul aria-label="Attendus travaillés" className="mt-2 flex flex-col divide-y divide-slate-100">
                    {visibles.map((f) => (
                        <li key={f.cle}>
                            <button
                                type="button"
                                onClick={(e) => onOuvrir(f, e.currentTarget)}
                                className="w-full flex items-center justify-between gap-3 py-1.5 text-left text-xs hover:text-blue-800"
                            >
                                <span className="min-w-0 truncate text-slate-800"><MathText>{f.titre}</MathText></span>
                                <span className="shrink-0 text-slate-500 whitespace-nowrap">{besoins(f)}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="mt-2 text-xs text-slate-500">Aucun attendu ne correspond.</p>
            )}
        </details>
    );
};

const Contenu = ({ isError, data, analyse, onOuvrir }) => {
    if (isError) return <p className="text-xs text-slate-500">L'analyse n'est pas disponible pour le moment.</p>;
    if (!data) return <p className="text-xs text-slate-400">Analyse en cours…</p>;
    const { priorites, nbPriorites, signaux, nbSignaux, tous } = analyse;
    return (
        <>
            {priorites.length ? (
                <div className="flex flex-col gap-2">
                    <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-rose-700">À reprendre en priorité · {nbPriorites}</p>
                        <p className="text-[11px] text-slate-500">Les notions où plusieurs élèves sont à reprendre.</p>
                    </div>
                    <ul aria-label="Priorités" className="flex flex-col gap-2">
                        {priorites.map((c) => <Proposition key={c.cle} carte={c} onOuvrir={onOuvrir} />)}
                    </ul>
                </div>
            ) : null}
            {signaux.length ? (
                // Repliés quand il y a des priorités ; ouverts sinon : des signaux ne laissent jamais l'écran vide.
                <details className="flex flex-col gap-2" open={!priorites.length}>
                    <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                        {/* Le chevron sur la ligne du titre, centré sur elle ; la phrase dessous commence sous le titre. */}
                        <span className="flex items-center gap-1.5">
                            <ChevronRight aria-hidden="true" className={`h-3 w-3 shrink-0 text-amber-700 ${CHEVRON_OUVERT}`} />
                            <span className="text-[11px] font-bold uppercase tracking-wide text-amber-700">Signaux à confirmer · {nbSignaux}</span>
                        </span>
                        <span className="block pl-[18px] text-[11px] text-slate-500">Des erreurs récentes chez plusieurs élèves, à confirmer.</span>
                    </summary>
                    <ul aria-label="Signaux" className="mt-2 flex flex-col gap-2">
                        {signaux.map((c) => <Proposition key={c.cle} carte={c} onOuvrir={onOuvrir} />)}
                    </ul>
                </details>
            ) : null}
            {!priorites.length && !signaux.length ? (
                // Pas d'alerte ne veut pas dire que tout va bien : il peut manquer des données.
                <p className="text-xs text-slate-500">
                    Aucune alerte à afficher avec les données disponibles.{tous.length ? ' Retrouvez le détail des élèves dans « Choisir un attendu ».' : ''}
                </p>
            ) : null}
            <ChoisirAttendu tous={tous} onOuvrir={onOuvrir} />
        </>
    );
};

const AnalyseTanoEd = () => {
    const { idclasse } = useParams();
    const { classeNom, groupNom } = useClasseContext();
    const { user, matiereFilter, etablissementData } = useUser();
    const navigate = useNavigate();
    const [ouverte, setOuverte] = useState(null);
    const declencheur = useRef(null);

    const complet = useMemo(
        () => Boolean(user && etablissementData && getAccessType(parseInt(idclasse, 10), user, etablissementData) === 'full'),
        [idclasse, user, etablissementData],
    );
    const { data, isError } = useBilanAttendus(idclasse, {
        estGroupe: !classeNom && Boolean(groupNom),
        matiere: user?.role === 'chef' ? matiereFilter : '',
        actif: complet,
    });
    // Toutes les propositions : une ligne chacune, elles se lisent d'un coup d'œil.
    const analyse = useMemo(() => analyseTanoEd(data, { nbCartes: Infinity }), [data]);
    const carteOuverte = ouverte ? analyse.tous.find((f) => f.cle === ouverte) : null;
    const ouvrir = useCallback((carte, element) => { declencheur.current = element; setOuverte(carte.cle); }, []);
    // À la fermeture, le focus revient à la ligne qui avait ouvert la fenêtre.
    const fermer = useCallback(() => {
        setOuverte(null);
        if (declencheur.current) declencheur.current.focus();
    }, []);
    // Vers le composeur (Créer), avec la liste d'élèves que la fenêtre vient de montrer. Le pré-remplissage
    // est l'étape suivante : Créer le lira le jour venu.
    const ouvrirCreer = (carte) => navigate(`/home/qcm/${idclasse}`, {
        state: { depuisAnalyse: { attendu: carte.attendu, code: carte.cible.code, eleves: carte.cible.eleves } },
    });

    if (!complet) return null;

    return (
        <div className="h-full rounded-2xl bg-gradient-to-b from-white to-sky-50/60 border border-slate-200 shadow-lg shadow-blue-500/10 overflow-hidden">
            <div className="flex flex-col h-full">
                <div className="shrink-0 flex items-center justify-between gap-3 px-5 py-4">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shadow-sm">
                            <ScanSearch className="w-[18px] h-[18px] text-blue-600" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-slate-800">L'analyse de TanoEd</h3>
                            <p className="text-[11px] text-slate-400">Les résultats des élèves et leurs erreurs des 14 derniers jours</p>
                        </div>
                    </div>
                    {data && analyse.tous.length ? (
                        <span className="hidden sm:flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-600 shadow-sm">
                            <BarChart3 aria-hidden="true" className="h-3.5 w-3.5 text-blue-600" />
                            Basé sur {pluriel(analyse.tous.length, 'attendu travaillé', 'attendus travaillés')}
                        </span>
                    ) : null}
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-4 flex flex-col gap-3">
                    <Contenu isError={isError} data={data} analyse={analyse} onOuvrir={ouvrir} />
                </div>
            </div>
            {carteOuverte ? <FenetreAttendu carte={carteOuverte} onFermer={fermer} ouvrirCreer={ouvrirCreer} /> : null}
        </div>
    );
};

export default AnalyseTanoEd;
